import { api, httpClient } from './api';
import { grundHeader } from '../components/ui/loeschen-mit-grund-dialog';

export interface TrialRequestItem {
  id: number;
  standort_id: number;
  vorname: string;
  nachname: string;
  email: string;
  wp_user_id?: number | null;
  subprofile_id?: string | null;
  telefon?: string | null;
  fach_id?: number | null;
  bevorzugte_zeiten?: string | null;
  notizen?: string | null;
  status: 'neu' | 'in_bearbeitung' | 'konvertiert' | 'abgelehnt' | string;
  ausgeblendet_grund?: string | null;
  ausgeblendet_notiz?: string | null;
  ausgeblendet_am?: string | null;
  existing_person?: boolean | null;
  existing_person_wp_user_id?: number | null;
  /** `personen.id` — nur damit lässt sich die Personenverwaltung aufrufen. */
  existing_person_id?: number | null;
  existing_person_source?: 'linked_account' | 'email_match' | string | null;
  existing_account_linked?: boolean | null;
  existing_person_email_match_wp_user_id?: number | null;
  created_at?: string;
  last_history_description?: string | null;
  /** Details des jüngsten Ereignisses, z. B. der Text eines Kommentars (T-0018). */
  last_history_details?: string | null;
  last_history_created_at?: string | null;
  /** Wer sich die Anfrage vorgemerkt hat (T-0014). */
  favoriten?: Array<{ accountId: string; name: string }>;
  /** Ob ich selbst darunter bin. */
  ist_favorit?: boolean;
}

/**
 * Drei Wege, eine Anfrage an den Bestand zu hängen:
 * - ``link_existing`` — ein bestehendes Schülerprofil (auch das des Kunden selbst)
 * - ``neues_profil`` — bekannter Kunde, neues Schülerprofil
 * - ``create_new`` — neuer Kunde; ``schueler`` nur, wenn nicht er selbst lernt
 */
export interface ResolveTrialRequestMergePayload {
  mode: 'link_existing' | 'neues_profil' | 'create_new';
  subprofileId?: string;
  personId?: number | null;
  kunde?: Record<string, unknown>;
  schueler?: Record<string, unknown>;
  // Ältere Aufrufer schickten die Kundenfelder flach; der Server versteht beides.
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  relationshipType?: string | null;
  relationshipCustom?: string | null;
}

export interface ListTrialRequestsParams {
  standort_id?: number;
  status?: string;
  q?: string;
  with_existing_person?: boolean;
  /** Nur die Anfragen, die ich mir vorgemerkt habe (T-0015). */
  favoritesOnly?: boolean;
}

export interface ConvertTrialRequestPayload {
  subprofileId?: string;
  linkedSubprofileId?: string;
  // Wird serverseitig gelesen, stand aber nie im Typ — der Fehler blieb hinter
  // einer zweiten fehlenden Eigenschaft desselben Literals verborgen.
  historyTemplateKey?: string;
  /** Der im Assistenten angezeigte Text — er wird verschickt, nicht die Vorlage. */
  drafts?: Record<string, { subject: string; html: string; text: string; sms: string }>;
  fachId: number;
  teacherId?: number | null;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  roomId?: number | null;
  sendEmails?: boolean;
  skipStudentEmail?: boolean;
  skipTeacherEmail?: boolean;
  sendSms?: boolean;
  skipStudentSms?: boolean;
  skipTeacherSms?: boolean;
  participant?: {
    isCompany?: boolean;
    companyName?: string | null;
    firstName: string;
    lastName: string;
    birthDate?: string | null;
    salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma' | null;
    structuredAddress?: {
      street?: string;
      postalCode?: string;
      zip?: string;
      city?: string;
      country?: string;
    };
    address?: string | null;
    email: string;
    phone?: string | null;
  };
}

export interface TrialRequestEmailCheckResponse {
  conflict: boolean;
  available: boolean;
  matchedWpUserId?: number | null;
  message?: string;
}

export interface TrialRequestHistoryItem {
  id: number;
  trialRequestId: number;
  type: string;
  description: string;
  details?: string | null;
  meta?: unknown | null;
  actorUserId?: number | null;
  actorName?: string | null;
  actorEmail?: string | null;
  createdAt: string;
}

export interface CreateTrialRequestHistoryPayload {
  type: string;
  description: string;
  details?: string | null;
  meta?: unknown | null;
}

export type TrialRequestHideReasonKey = 'not_reachable' | 'no_trial_wanted' | 'already_scheduled';

export interface HideTrialRequestPayload {
  reasonKey: TrialRequestHideReasonKey;
  historyTemplateKey?: string;
  note?: string | null;
  skipSend?: boolean;
  channels?: {
    email?: boolean;
    sms?: boolean;
  };
  /** Der im Assistenten angezeigte Text — er wird verschickt, nicht die Vorlage. */
  drafts?: { requester?: { subject: string; html: string; text: string; sms: string } };
  followUp?: {
    enabled: boolean;
    dueAt: string; // YYYY-MM-DD HH:MM:SS
    assignedUserId?: number;
    description?: string;
  };
}

type TrialRequestConvertResponse = Record<string, unknown>;
type TrialRequestHideResponse = TrialRequestItem & {
  emailOk?: boolean | null;
  emailError?: string | null;
  smsOk?: boolean | null;
  smsError?: string | null;
  smsBody?: string | null;
  taskId?: number | null;
  taskOk?: boolean | null;
  reasonLabel?: string;
};

export const trialRequestsApi = {
  list: async (params: ListTrialRequestsParams = {}): Promise<TrialRequestItem[]> => {
    const qs = new URLSearchParams();
    if (params.standort_id !== undefined && params.standort_id !== null) qs.set('standort_id', String(params.standort_id));
    if (params.status) qs.set('status', String(params.status));
    if (params.q) qs.set('q', String(params.q));
    if (params.with_existing_person) qs.set('with_existing_person', '1');
    if (params.favoritesOnly) qs.set('favoritesOnly', '1');
    return api.get<TrialRequestItem[]>(`/api/trial-requests?${qs.toString()}`);
  },
  /** Vormerken oder die Markierung wieder abnehmen (T-0013). */
  setFavorit: async (
    id: number,
    favorit: boolean
  ): Promise<{ id: number; ist_favorit: boolean; favoriten: Array<{ accountId: string; name: string }> }> => {
    const res = await httpClient<{ id: number; ist_favorit: boolean; favoriten: Array<{ accountId: string; name: string }> }>(
      `/api/trial-requests/${id}/favorit`,
      { method: 'PUT', body: JSON.stringify({ favorit }) } as RequestInit
    );
    if (res.error || !res.data) throw new Error(res.error || 'Favorit konnte nicht gespeichert werden');
    return res.data;
  },
  get: async (id: number): Promise<TrialRequestItem> => {
    return api.get<TrialRequestItem>(`/api/trial-requests/${id}`);
  },
  update: async (id: number, data: Partial<TrialRequestItem>): Promise<TrialRequestItem> => {
    const res = await httpClient<TrialRequestItem>(`/api/trial-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Aktualisieren der Anfrage');
    return res.data;
  },
  convert: async (id: number, payload: ConvertTrialRequestPayload): Promise<TrialRequestConvertResponse | null> => {
    const res = await httpClient<TrialRequestConvertResponse>(`/api/trial-requests/${id}/convert`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Fehler beim Konvertieren der Anfrage');
    return res.data ?? null;
  },
  checkEmail: async (id: number, email: string, linkedSubprofileId?: string): Promise<TrialRequestEmailCheckResponse> => {
    const res = await httpClient<TrialRequestEmailCheckResponse>(`/api/trial-requests/${id}/email-check`, {
      method: 'POST',
      body: JSON.stringify({
        email,
        linkedSubprofileId: linkedSubprofileId || undefined,
      }),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'E-Mail-Prüfung fehlgeschlagen');
    return res.data;
  },
  resolveMerge: async (id: number, payload: ResolveTrialRequestMergePayload): Promise<TrialRequestItem> => {
    const res = await httpClient<TrialRequestItem>(`/api/trial-requests/${id}/resolve-merge`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Datensatz-Verknüpfung fehlgeschlagen');
    return res.data;
  },
  hide: async (id: number, payload: HideTrialRequestPayload): Promise<TrialRequestHideResponse> => {
    const res = await httpClient<TrialRequestHideResponse>(`/api/trial-requests/${id}/hide`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Ausblenden der Anfrage');
    return res.data;
  },
  /** Eine ausgeblendete Anfrage wieder einblenden (T-0063). */
  unhide: async (id: number): Promise<{ ok: boolean; status: string }> => {
    const res = await httpClient<{ ok: boolean; status: string }>(`/api/trial-requests/${id}/unhide`, {
      method: 'POST',
      body: JSON.stringify({}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Anfrage konnte nicht wieder eingeblendet werden');
    return res.data;
  },
  remove: async (id: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/trial-requests/${id}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Anfrage konnte nicht gelöscht werden');
  },
  history: {
    list: async (id: number): Promise<TrialRequestHistoryItem[]> => {
      return api.get<TrialRequestHistoryItem[]>(`/api/trial-requests/${id}/history`);
    },
    create: async (id: number, payload: CreateTrialRequestHistoryPayload): Promise<TrialRequestHistoryItem> => {
      const res = await httpClient<TrialRequestHistoryItem>(`/api/trial-requests/${id}/history`, {
        method: 'POST',
        body: JSON.stringify(payload),
      } as RequestInit);
      if (res.error || !res.data) throw new Error(res.error || 'Historien-Eintrag fehlgeschlagen');
      return res.data;
    },
    delete: async (id: number, entryId: number, grund: string): Promise<void> => {
      const res = await httpClient<unknown>(`/api/trial-requests/${id}/history/${entryId}`, {
        method: 'DELETE',
        ...grundHeader(grund),
      } as RequestInit);
      if (res.error) throw new Error(res.error || 'Historien-Eintrag konnte nicht gelöscht werden');
    },
  }
};

/**
 * Status, mit denen eine Anfrage nicht mehr in die offene Liste gehört: ausgeblendet,
 * in eine Probestunde umgewandelt oder ans Altsystem weitergeleitet (T-0062).
 */
const ERLEDIGTE_ANFRAGEN = new Set(['ausgeblendet', 'konvertiert', 'weitergeleitet_legacy']);

export const isOffeneAnfrage = (item: { status?: string | null }): boolean =>
  !ERLEDIGTE_ANFRAGEN.has(String(item.status || '').toLowerCase());
